/**
 * FlexEngine — PWA Registration & Update Management
 *
 * Uses vite-plugin-pwa's virtual module for SW lifecycle management.
 * Exposes an "update available" signal consumed by the app shell.
 *
 * Offline strategy (configured in vite.config.ts via Workbox):
 *   - All JS/CSS/HTML: CacheFirst (pre-cached at install time)
 *   - IndexedDB (Dexie): always local, zero network dependency
 *   - No runtime network calls — 100% offline capable
 */

import { registerSW } from 'virtual:pwa-register';

export type UpdateReadyCallback = (updateFn: () => void) => void;

let _onUpdateReady: UpdateReadyCallback | null = null;

/**
 * Register a callback that fires when a new SW version is waiting.
 * The callback receives an `updateFn` — call it to activate the update.
 */
export function onUpdateReady(cb: UpdateReadyCallback): void {
  _onUpdateReady = cb;
}

/**
 * Call once at app startup (main.tsx).
 * Returns an `updateSW` function that can force-activate the waiting SW.
 */
export function initPWA(): () => void {
  const updateSW = registerSW({
    onNeedRefresh() {
      // A new SW is waiting — notify the UI
      _onUpdateReady?.(() => updateSW(true));
    },
    onOfflineReady() {
      console.info('[FlexEngine] App is ready for offline use ✓');
    },
    onRegistered(r) {
      // Poll for updates every 60 minutes — useful in gym environments
      // where the app may be open all day
      if (r) {
        setInterval(() => r.update(), 60 * 60 * 1000);
      }
    },
    onRegisterError(error) {
      console.warn('[FlexEngine] SW registration failed:', error);
    },
  });

  return updateSW;
}
